import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, ButtonGroup, Grid, CircularProgress } from '@mui/material';

import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';

import PagedTable from 'components/table/PagedTable';
import { filterSelector, bookingModeSelector } from 'helper/filter';
import { userSelector, hasAnyAdminSpace } from 'helper/security';
import { EBookingMode } from 'types';

import { FACILITIES_LIST_QUERY } from './gql';

import { FacilityCopyButton, FacilityDeleteButton } from './facility';
import LocalizedSkuText from './LocalizedSkuText';

/**
 * Facilities list page with pagination and filtering by space and booking mode.
 * Supports copy and delete actions for facilities.
 */
export default function FacilitiesList() {
  const navigate = useNavigate();
  const filter = filterSelector();
  const bookingMode = bookingModeSelector();
  const user = userSelector()!;

  const [query, { data, loading, error }] = useLazyQuery(FACILITIES_LIST_QUERY);

  return (
    <>
      <Helmet>
        <title>{i18next.t('facilities-list-page-title')}</title>
      </Helmet>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PagedTable
            filterKey={`${JSON.stringify(filter)}`}
            refetchPage={(skip, take) =>
              query({
                variables: {
                  spaceId: (filter && filter.spaceId) || null,
                  where: {
                    ...(bookingMode ? { forLister: bookingMode === EBookingMode.EPILO } : {}),
                  },
                  skip,
                  take,
                },
              })
            }
            rowsLoading={loading}
            rowsErr={error}
            headers={[
              i18next.t('facilities-list-header-facility'),
              i18next.t('facilities-list-header-sku'),
              i18next.t('facilities-list-header-recurring'),
              i18next.t('facilities-list-header-isaddon'),
              ...(user.isSingleSpace ? [] : [i18next.t('list-header-space')]),
              '',
            ].filter(f => f !== undefined)}
            totalCount={data?.listFacilities?.totalCount}
            rows={(data?.listFacilities?.facilities || []).map((row, index) =>
              [
                <Link to={`/products/facilities/${row.id}`}>
                  <LocalizedSkuText
                    sku={row.sku}
                    fallback={row.name}
                    hotelId={filter?.hotelId || null}
                    spaceId={filter?.spaceId || row.space.id}
                  />
                  {row.extRefCode && ` (${row.extRefCode})`}
                </Link>,
                row.sku,
                `${i18next.t('enums-EFacilityPayment-' + row.recurring)}`,
                row.isAddon ? <CheckIcon /> : '',
                ...(user.isSingleSpace ? [] : [row.space.name]),
                <ButtonGroup>
                  <FacilityCopyButton id={row.id} spaceId={row.space.id} icon />
                  <FacilityDeleteButton id={row.id} space={row.space} isSystem={row.isSystem} forLister={row.forLister} icon />
                </ButtonGroup>,
              ].filter(f => f !== undefined),
            )}
          />
        </Grid>
        {hasAnyAdminSpace(user) && (
          <Grid item xs={12}>
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!filter?.spaceId}
              onClick={() => navigate('/products/facilities/_create')}
            >
              {i18next.t('facilities-list-add')}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
}
